import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productService } from '../services/productService';
import { getImageUrl } from '../utils/imageHelper';
import Loading from '../components/common/Loading';
import './Products.css';

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await productService.getAllProducts({
        page: pagination.page,
        limit: pagination.limit,
      });

      setProducts(data.products || []);
      setPagination((prev) => ({
        ...prev,
        ...data.pagination,
      }));
    } catch (err) {
      let errorMessage = 'Failed to load products';
      if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"?`)) {
      return;
    }

    try {
      await productService.deleteProduct(productId);
      // Reload products after deletion
      loadProducts();
    } catch (err) {
      alert('Failed to delete product: ' + (err.message || 'Unknown error'));
    }
  };


  const getPrimaryImage = (images) => {
    if (!images || images.length === 0) return null;
    const primary = images.find((img) => img.is_primary);
    return primary || images[0];
  };

  if (loading) {
    return <Loading message="Loading products..." />;
  }

  return (
    <div className="products-container">
      <div className="page-header">
        <h1 className="page-title">Products</h1>
        <Link to="/products/create" className="btn-primary">
          Create Product
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="products-content">
        {products.length === 0 ? (
          <div className="empty-state">
            <p>No products found.</p>
            <Link to="/products/create" className="btn-primary">
              Create Your First Product
            </Link>
          </div>
        ) : (
          <>
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>SKU</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const primaryImage = getPrimaryImage(product.images);

                    // Use getImageUrl helper to properly handle CDN URLs
                    const imageUrl = primaryImage?.image_url
                      ? getImageUrl(primaryImage.image_url)
                      : null;

                    return (
                      <tr key={product.id}>
                        <td>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={primaryImage?.alt_text || product.name}
                              className="product-thumbnail"
                              onError={(e) => {
                                console.error('Image failed to load:', e.target.src);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </td>
                        <td>
                          <div className="product-name">{product.name}</div>
                          {product.description && (
                            <div className="product-description">
                              {product.description.replace(/<[^>]*>/g, '').substring(0, 50)}
                              {product.description.replace(/<[^>]*>/g, '').length > 50 ? '...' : ''}
                            </div>
                          )}
                        </td>
                        <td>{product.sku || '-'}</td>
                        <td>
                          {product.discounted_price ? (
                            <>
                              <div>Rs. {parseFloat(product.discounted_price).toLocaleString()}</div>
                              <div style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.875rem' }}>
                                Rs. {parseFloat(product.price).toLocaleString()}
                              </div>
                            </>
                          ) : (
                            <div>Rs. {parseFloat(product.price).toLocaleString()}</div>
                          )}
                        </td>
                        <td>
                          <span
                            className={`stock-badge ${product.stock_quantity > 0 ? 'in-stock' : 'out-of-stock'
                              }`}
                          >
                            {product.stock_quantity}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${product.status}`}>
                            {product.status}
                          </span>
                        </td>
                        <td>{product.category?.name || '-'}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => navigate(`/products/edit/${product.id}`)}
                              className="btn-edit"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(product.id, product.name)}
                              className="btn-delete"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  disabled={pagination.page >= pagination.pages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Products;
